import { useState, useMemo } from 'react';
import { Users, ChevronLeft, ChevronRight, User } from 'lucide-react';
import { useStore } from '../store/useStore';
import { StudentCard } from './StudentCard';

export function StudentDashboard() {
  const { studentDataList } = useStore();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const sortedStudents = useMemo(() => {
    return [...studentDataList].sort((a, b) => {
      if (a.student.groupId !== b.student.groupId) {
        return a.student.groupId.localeCompare(b.student.groupId);
      }
      return a.student.studentId.localeCompare(b.student.studentId);
    });
  }, [studentDataList]);

  const selectedStudent = sortedStudents[selectedIndex];

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev < sortedStudents.length - 1 ? prev + 1 : prev));
  };

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
  };

  if (studentDataList.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12">
        <div className="text-center text-gray-400">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">暂无学生数据，请先上传Excel文件</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-full">
      <div className="w-80 bg-white rounded-xl shadow-lg overflow-hidden flex flex-col">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Users className="w-4 h-4" />
            学生列表
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            共 {sortedStudents.length} 名学生
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {sortedStudents.map((student, index) => (
            <button
              key={student.student.studentId}
              onClick={() => handleSelect(index)}
              className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors border-b border-gray-100 hover:bg-gray-50 ${
                index === selectedIndex ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                index === selectedIndex ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                <User className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-medium truncate ${index === selectedIndex ? 'text-blue-700' : 'text-gray-800'}`}>
                  {student.student.studentName}
                </div>
                <div className="text-xs text-gray-500">
                  {student.student.groupId} | {student.student.studentId.split('_')[1]}
                </div>
              </div>
              {index === selectedIndex && (
                <div className="text-xs text-blue-500 font-medium">
                  查看中
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-600">
            当前显示: <span className="font-medium text-gray-800">{selectedStudent?.student.studentName}</span>
            <span className="mx-2">|</span>
            第 {selectedIndex + 1} / {sortedStudents.length} 名
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={selectedIndex === 0}
              className="p-2 rounded-lg bg-white shadow hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={handleNext}
              disabled={selectedIndex === sortedStudents.length - 1}
              className="p-2 rounded-lg bg-white shadow hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {selectedStudent && <StudentCard data={selectedStudent} />}
        </div>
      </div>
    </div>
  );
}